import React from 'react';
import { ArrowRight, Car, ExternalLink, Mail, MapPin, Navigation, PhoneCall, Send } from 'lucide-react';
import DecorLeaf from './DecorLeaf';
import { STORE } from '../config/store';
import { homeRail, focusRing } from '../styles/ui';

// Every fact here comes from config/store.js, the single approved source for
// the store's details. The project holds no email, opening hours, parking
// information or social accounts, so this section shows none — the
// reference's examples of those are design, not data. The "Email Us" card
// appears only if an email is ever published in STORE.
//
// The map panel is the design's own artwork (/home/store-map-panel.webp); the
// real action — opening the store in Google Maps — is the same as always.
const parts = STORE.address.split(',').map((part) => part.trim());
// "…Bhartiya City Back Gate Rd" and "Devin Paradise Enclave" are the second
// and third parts of the published address, shown as landmarks.
const ROAD = parts[2] || parts[0];
const LANDMARK = parts[3] || parts[1] || '';
const LOCALITY = parts[4] || parts[parts.length - 2] || '';

const infoCard = 'flex items-center gap-3 min-h-[60px] rounded-2xl bg-white border border-[#E6EEE6] shadow-[0_2px_10px_-4px_rgba(15,51,24,0.12)] px-3 py-2.5';
const infoIcon = 'w-10 h-10 rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0';

const Contact = () => {
  const openMaps = () => {
    window.open(STORE.mapsUrl, '_blank', 'noopener,noreferrer');
  };

  const cards = [
    { icon: MapPin, title: 'Our Location', body: <address className="not-italic">{STORE.address}</address> },
    {
      icon: PhoneCall,
      title: 'Phone Number',
      body: (
        <a href={STORE.phoneHref} className={`hover:text-emerald-800 ${focusRing}`}>
          {STORE.phone}
        </a>
      ),
    },
    ...(STORE.email
      ? [{ icon: Mail, title: 'Email Us', body: <a href={`mailto:${STORE.email}`}>{STORE.email}</a> }]
      : []),
  ];

  return (
    <section
      id="contact"
      aria-labelledby="contact-heading"
      className="relative overflow-hidden pt-5 lg:pt-[26px] pb-7 lg:pb-[34px] bg-[linear-gradient(180deg,#FFFFFF_0%,#F7FBF5_100%)]"
    >
      <div className="pointer-events-none select-none absolute inset-0" aria-hidden="true">
        <img src="/home/contact-crate.webp" alt="" loading="lazy" width="300" height="500" className="hidden xl:block absolute left-0 top-[36px] w-[255px]" />
        <img src="/home/decor-contact-right.webp" alt="" loading="lazy" width="132" height="700" className="hidden xl:block absolute right-0 top-0 w-[112px]" />
        <DecorLeaf className="hidden xl:block absolute left-[150px] top-[40px] w-8 text-emerald-500/80 rotate-[20deg]" />
        <DecorLeaf className="hidden xl:block absolute left-[650px] top-[90px] w-7 text-emerald-500/80 -rotate-[24deg]" />
      </div>

      <div className={`${homeRail} relative`}>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] xl:grid-cols-[396px_725px] gap-8 xl:gap-[52px] xl:pl-[151px] items-start">
          <div>
            <span className="inline-flex items-center gap-1.5 h-[30px] px-4 rounded-full bg-[#E9F5E4] text-emerald-800 text-[10.5px] font-bold tracking-[0.16em] uppercase">
              <DecorLeaf className="w-3.5 text-emerald-600" />
              Get in Touch
            </span>
            <h2 id="contact-heading" className="serif mt-[12px] text-[34px] sm:text-[40px] xl:text-[44px] font-bold leading-[1.08] text-emerald-950">
              We&apos;d Love to
              <br />
              <span className="text-[#1E8A3B]">Hear from You!</span>
            </h2>
            <p className="mt-[10px] text-[14px] leading-[20px] text-[#4B5563]">
              Have questions, bulk order requirements, or partnership inquiries? Our team is here to
              help. Reach out to us anytime.
            </p>

            <ul className="mt-[16px] flex flex-col gap-2">
              {cards.map(({ icon: Icon, title, body }) => (
                <li key={title} className={`${infoCard} min-h-[77px]`}>
                  <span className={infoIcon}>
                    <Icon size={19} strokeWidth={1.9} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 text-[12.5px] leading-[1.4] text-[#4B5563]">
                    <span className="block text-[13.5px] font-bold text-emerald-950 mb-[2px]">{title}</span>
                    {body}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-[12px] grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={openMaps}
                className={`inline-flex items-center justify-center gap-2 h-[40px] rounded-full bg-[linear-gradient(90deg,#3FA84A,#2E8B47)] text-white text-[13.5px] font-bold shadow-[0_8px_18px_-6px_rgba(46,139,71,0.55)] hover:brightness-105 ${focusRing}`}
              >
                <Navigation size={15} aria-hidden="true" />
                Get Directions
                <ArrowRight size={14} strokeWidth={2.5} aria-hidden="true" />
              </button>
              <a
                href={STORE.phoneHref}
                className={`inline-flex items-center justify-center gap-2 h-[40px] rounded-full bg-white border border-[#2E8B47] text-[#1E5A30] text-[13.5px] font-bold hover:bg-emerald-50 ${focusRing}`}
              >
                <PhoneCall size={15} aria-hidden="true" />
                Call Now
              </a>
            </div>
          </div>

          {/* Store / map panel */}
          <div className="relative xl:mt-[11px]">
            <button
              type="button"
              onClick={openMaps}
              aria-label={`Open ${STORE.name} in Google Maps`}
              className={`group block w-full overflow-hidden rounded-[22px] border border-[#E6EEE6] shadow-[0_14px_36px_-16px_rgba(15,51,24,0.3)] ${focusRing}`}
            >
              <img
                src="/home/store-map-panel.webp"
                alt=""
                loading="lazy"
                width="852"
                height="508"
                className="w-full h-auto xl:h-[432px] object-cover object-left-top transition-transform duration-500 group-hover:scale-[1.02]"
              />
            </button>

            {/* Store card */}
            <div className="mt-3 xl:mt-0 xl:absolute xl:left-[19px] xl:top-[19px] xl:w-[378px] flex gap-3 rounded-2xl bg-white border border-[#E6EEE6] shadow-[0_10px_24px_-12px_rgba(15,51,24,0.3)] p-3">
              <img src="/home/store-front.webp" alt={`${STORE.name} storefront`} loading="lazy" width="110" height="125" className="w-[94px] h-[106px] rounded-xl object-cover shrink-0" />
              <span className="min-w-0">
                <span className="flex items-center gap-2 flex-wrap">
                  <span className="serif text-[17px] font-bold text-emerald-950 leading-tight">{STORE.name}</span>
                  <span className="inline-flex items-center h-5 px-2 rounded-full bg-[#DFF3DA] text-emerald-800 text-[10px] font-bold">Our Store</span>
                </span>
                <address className="mt-1 text-[11.5px] leading-[1.4] text-[#4B5563] not-italic">{STORE.address}</address>
                <a
                  href={STORE.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#1E7A3C] hover:text-emerald-900 ${focusRing}`}
                >
                  <MapPin size={12} aria-hidden="true" />
                  View on Google Maps
                  <ExternalLink size={11} aria-hidden="true" />
                </a>
              </span>
            </div>

            {/* Reaching the store — every line is a part of the published address. */}
            <ul className="mt-3 xl:mt-0 grid grid-cols-1 sm:grid-cols-3 xl:block gap-2 xl:absolute xl:right-[18px] xl:top-[27px] xl:w-[195px] xl:space-y-2">
              <li className={infoCard}>
                <span className={infoIcon}><Car size={18} aria-hidden="true" /></span>
                <span className="min-w-0 text-[10.5px] leading-[1.35] text-[#4B5563]"><span className="block text-[12px] font-bold text-emerald-950">Easy to Reach</span>Located on {ROAD}</span>
              </li>
              <li className={infoCard}>
                <span className={infoIcon}><MapPin size={18} aria-hidden="true" /></span>
                <span className="min-w-0 text-[10.5px] leading-[1.35] text-[#4B5563]"><span className="block text-[12px] font-bold text-emerald-950">Locality</span>{LOCALITY}</span>
              </li>
              <li className={infoCard}>
                <span className={infoIcon}><Send size={18} aria-hidden="true" /></span>
                <span className="min-w-0 text-[10.5px] leading-[1.35] text-[#4B5563]"><span className="block text-[12px] font-bold text-emerald-950">Landmark</span>{LANDMARK}</span>
              </li>
            </ul>

            {/* Visit Us */}
            <div className="mt-3 xl:mt-0 xl:absolute xl:right-[-37px] xl:bottom-[10px] xl:w-[295px] flex items-center gap-3 rounded-2xl bg-white border border-[#E6EEE6] shadow-[0_14px_30px_-14px_rgba(15,51,24,0.35)] p-3">
              <img src="/home/visit-scooter.webp" alt="" aria-hidden="true" loading="lazy" width="137" height="109" className="w-[110px] h-auto shrink-0" />
              <span>
                <span className="serif block text-[17px] font-bold text-emerald-950">Visit Us</span>
                <span className="block mt-1 text-[12px] leading-[1.35] text-[#4B5563]">We&apos;re always happy<br />to welcome you!</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
